import { baseUrl } from '@/constants';
import type { SseJobCallbacks, SseJobEvent } from '@/types';

/**
 * The transport for every long-running project job (document import,
 * logline/synopsis generation). All jobs publish on the same global Redis
 * channel (`storyliner-events`) and the same HTTP stream. This module owns the
 * transport quirks — the stream never closes on its own, keep-alives every
 * ~10s, reconnect with backoff, `project_id` tagging to tell jobs apart — so
 * each job's mutation only decides what an event means and which event is
 * terminal. `runSseJob` (sibling module) sequences a job's POST against it.
 */

/** Reaching 100% is terminal for every job on the channel. */
export const isSseJobComplete = (event: SseJobEvent) => event.type === 'progress' && event.percent >= 100;

// Hard cap on one job's lifetime. Every job streams one DeepSeek completion
// over the whole script (the DeepSeekService Guzzle timeout is ~17 min), so
// they share one budget.
export const SSE_JOB_TTL_MS = 30 * 60 * 1000;

const MAX_SSE_RETRIES = 3;

// How often the read loop wakes up to evaluate the deadline/idle checks when no
// data arrives. Keep-alives come every ~10s, so a tick mostly loses the race.
const READ_TICK_MS = 15 * 1000;

// Abort a connection attempt that gets no response headers. Each SSE stream
// ties up a backend PHP worker indefinitely, so a depleted worker pool accepts
// the TCP connection but never answers — without this cap fetch() hangs
// forever and the caller's locks / resume markers are never released.
const CONNECT_TIMEOUT_MS = 30 * 1000;

// A healthy stream carries keep-alives every ~10s. If nothing at all (not even
// a keep-alive) arrives for this long, the connection is mute: the terminal
// event could never reach us, so waiting on it would be waiting blind.
// Reconnect instead; the retry cap ends the wait if every connection stays mute.
const LIVENESS_TIMEOUT_MS = 45 * 1000;

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

function parseSseEvents(text: string): string[] {
    return text
        .split('\n\n')
        .filter(Boolean)
        .map(block => {
            const dataLine = block.split('\n').find(line => line.startsWith('data:'));
            return dataLine ? dataLine.slice(5).trim() : '';
        })
        .filter(Boolean);
}

// Server messages are JSON-encoded strings:
//   {"type": "event", "status": "success" | "error", "message": "...", "project_id": 123}
//   {"type": "progress", "status": "38", "message": "...", "project_id": 123} — status is a percentage
//   {"type": "keep-alive", ...} — published every 10s by KeepAliveSSEJob, not for display, no project_id
//
// The Redis channel is global — every project's job publishes on it — and jobs
// are unique per project, so two different projects can run jobs at the same
// time. `project_id` is how a listener tells its own job's messages apart from
// another project's traffic on the same channel. A message with no project_id
// (a job's very first push, before it tags) is treated as ours.
export function parseSseJobEvent(raw: string, projectId: number): SseJobEvent | null {
    try {
        const data = JSON.parse(raw);
        if (data?.type === 'keep-alive') {
            return null;
        }
        // Another project's job — not for this listener, but still proves
        // the channel is alive (handled by the caller via lastMessageAt).
        if (typeof data?.project_id === 'number' && data.project_id !== projectId) {
            return null;
        }
        if (data?.type === 'progress') {
            const percent = Number(data.status);
            return { type: 'progress', percent: Number.isFinite(percent) ? percent : 0, message: String(data.message ?? '') };
        }
        if (data?.type === 'event') {
            return { type: 'event', status: data.status === 'error' ? 'error' : 'success', message: String(data.message ?? '') };
        }
    } catch {
        // Not JSON — fall through and show the raw text.
    }
    return { type: 'event', status: 'success', message: raw };
}

export async function listenSse({
    token,
    projectId,
    signal,
    deadlineAt,
    idleTimeoutMs,
    initialIdleTimeoutMs,
    isFinished = isSseJobComplete,
    timeoutMessage,
    onSseEvent,
    onSseError,
    onSseComplete,
}: {
    token: string | null;
    /** The project this listener tracks — the shared channel carries every project's traffic, so messages for other projects are ignored. */
    projectId: number;
    signal: AbortSignal;
    /** Absolute timestamp after which we stop waiting and report `timeoutMessage`. */
    deadlineAt: number;
    /** If set, finish silently when no real (non-keep-alive) event arrives for this long — used by resume-after-refresh, where the job may have already finished. */
    idleTimeoutMs?: number;
    /** Idle window applied until the first real event of this listen. Lets resume count silence that already elapsed while the page was closed. Falls back to idleTimeoutMs. */
    initialIdleTimeoutMs?: number;
    /** Which event ends the job. Defaults to 100% progress; a job with its own terminal text (import's "Project imported") extends it. */
    isFinished?: (event: SseJobEvent) => boolean;
    /** Passed to `onSseError` when `deadlineAt` passes. */
    timeoutMessage: string;
} & SseJobCallbacks) {
    // Proxies/VPNs drop long-lived streams (Firefox: NS_ERROR_NET_PARTIAL_TRANSFER),
    // so reconnect with backoff instead of treating one drop as fatal.
    let attempts = 0;
    let lastRealEventAt = Date.now();
    let receivedRealEvent = false;

    while (true) {
        // The caller may abort during the retry backoff; an abort listener
        // added to an already-aborted signal never fires, so check explicitly.
        if (signal.aborted) return;

        // Per-attempt controller: lets the connect timeout abort a hung fetch
        // while still honoring the caller's signal for the attempt's lifetime.
        const attempt = new AbortController();
        const abortAttempt = () => attempt.abort();
        signal.addEventListener('abort', abortAttempt, { once: true });
        const connectTimer = setTimeout(abortAttempt, CONNECT_TIMEOUT_MS);

        try {
            const response = await fetch(`${baseUrl}sse-events`, {
                method: 'GET',
                signal: attempt.signal,
                headers: {
                    Accept: 'text/event-stream',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });
            // Headers arrived — the timeout only guards the connect phase; the
            // read phase is guarded by the READ_TICK_MS race below.
            clearTimeout(connectTimer);

            if (!response.ok || !response.body) {
                throw new Error(`SSE connection failed: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            // Any message counts, including keep-alives — this tracks channel
            // liveness, not job activity.
            let lastMessageAt = Date.now();
            // Kept across timed-out races: calling reader.read() again while one is
            // pending would queue a second read and silently swallow a chunk.
            let pendingRead: ReturnType<typeof reader.read> | null = null;

            try {
                while (true) {
                    // Race the read against a tick so the deadline/idle checks below
                    // run even when nothing arrives at all (e.g. the keep-alive
                    // worker is down) — otherwise read() blocks forever and the
                    // caller waits on it forever.
                    pendingRead ??= reader.read();
                    const result = await Promise.race([pendingRead, delay(READ_TICK_MS).then(() => null)]);

                    if (result) {
                        pendingRead = null;
                        if (result.done) {
                            // The server holds this stream open indefinitely (blocking
                            // Redis subscribe), so a close is a dropped connection,
                            // not completion.
                            throw new Error('SSE stream closed unexpectedly');
                        }

                        buffer += decoder.decode(result.value, { stream: true });

                        const messages = parseSseEvents(buffer);
                        const lastNewlineIdx = buffer.lastIndexOf('\n\n');
                        buffer = lastNewlineIdx >= 0 ? buffer.slice(lastNewlineIdx + 2) : buffer;

                        for (const msg of messages) {
                            attempts = 0;
                            lastMessageAt = Date.now();
                            const event = parseSseJobEvent(msg, projectId);
                            if (!event) continue; // keep-alive, or another project's message: proves the connection works, nothing to show

                            lastRealEventAt = Date.now();
                            receivedRealEvent = true;
                            onSseEvent(event);

                            if (isFinished(event)) {
                                onSseComplete();
                                return;
                            }
                            // An error event is terminal too: a job stops after
                            // publishing one (e.g. unparseable AI response), so
                            // the terminal event will never arrive.
                            if (event.type === 'event' && event.status === 'error') {
                                return;
                            }
                        }
                    }

                    if (Date.now() > deadlineAt) {
                        onSseError(timeoutMessage);
                        return;
                    }
                    // Mute connection — not even keep-alives. Reconnect rather
                    // than wait on a channel that can't deliver the terminal event.
                    if (Date.now() - lastMessageAt > LIVENESS_TIMEOUT_MS) {
                        throw new Error('SSE connection silent (no keep-alive)');
                    }
                    const idleLimit = receivedRealEvent ? idleTimeoutMs : (initialIdleTimeoutMs ?? idleTimeoutMs);
                    if (idleLimit && Date.now() - lastRealEventAt > idleLimit) {
                        onSseComplete();
                        return;
                    }
                }
            } finally {
                // Release the connection on every exit path (terminal event,
                // timeout, thrown error) — the resume listener has no outer abort.
                reader.cancel().catch(() => {});
            }
        } catch (error) {
            // Aborted by the caller (the job's POST failed) — not an SSE failure.
            // A connect-timeout abort leaves the caller's signal untouched and
            // falls through to the retry logic below.
            if (signal.aborted) return;

            if (Date.now() > deadlineAt) {
                onSseError(timeoutMessage);
                return;
            }

            attempts += 1;

            if (attempts > MAX_SSE_RETRIES) {
                import('@sentry/react').then(Sentry => {
                    Sentry.captureMessage(
                        `SSE connection lost after ${MAX_SSE_RETRIES} retries: ${error instanceof Error ? error.message : String(error)}`,
                        'warning',
                    );
                });
                onSseError('SSE connection lost');
                return;
            }

            await delay(1000 * 2 ** (attempts - 1));
        } finally {
            clearTimeout(connectTimer);
            signal.removeEventListener('abort', abortAttempt);
        }
    }
}
